import * as React from "react";
import { useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import { Add, RemoveCircleOutline } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import translations from "translations";
import { useGetUnitMatrixParametersQuery } from "graphqlBase/getUnitMatrixParameters";
import { useRecoilValue, atom } from "recoil";
import { MatrixParametersData, TimePowerParameters } from "pages/UploadCertificate/types";
import Props from "./index.d";
import MatrixParameterVisibilityModal from "components/formElements/fields/MatrixParameterVisibilityModal";
import { useMatrixParameterVisibility } from "../hooks/useMatrixParameterVisibility";
import {
  createEmptyTimePowerParameters,
  flattenRows,
  cleanMatrixParametersForSave,
  computeMinMax,
  replaceAll,
  useDecimalInputValidation,
  extractZwValuesFromLegacyData,
} from "./matrixDataUtils";
import {
  StyledTableContainer,
  StyledColumnHeaderCell,
  StyledAddRangeButton,
  StyledTextField,
} from "./styledComponents";
import MatrixParameterRow from "./MatrixParameterRow";

const P_FIELDS = new Set<keyof TimePowerParameters>([
  "pMinDyn",
  "pMaxDyn",
  "pGrenzNegMin",
  "pGrenzNegMax",
  "pGrenzPosMin",
  "pGrenzPosMax",
]);
import { useMatrixValidation, FieldIndices } from "./useMatrixValidation";
import { subscript } from "components/mlpaUiComponents/LegalNote/lib";

const MatrixParameters: React.FC<Props> = (props) => {
  const {
    title = "Matrix Parameters",
    data: { state, setstate },
    disabled = false,
    errorManager,
    className,
    getFieldValue,
    isEdit,
  } = props;

  // Create dummy atoms FIRST before use
  const dummyAtom = React.useMemo(
    () =>
      atom({
        key: `matrixParams-dummy-${Math.random()}`,
        default: null,
      }),
    []
  );

  const {
    showVisibilityControls,
    openVisibilityModal,
    setOpenVisibilityModal,
    hasVisibilityGrants,
    getVisibilityTooltip,
    handleVisibilitySave,
    currentRoles,
    currentTenantIds,
  } = useMatrixParameterVisibility({ getFieldValue });

  const errorManagerRes = errorManager({
    errorJoiner: (errors) => {
      return errors.join(", ");
    },
  });

  // Try to get unit ID from form context for fetching data
  const unitIdSelector = React.useMemo(() => {
    try {
      const idSelector = getFieldValue("id");
      const unitToReplaceIdSelector = getFieldValue("__unitToReplaceId");
      const unitIdSelector = getFieldValue("unitId");

      // Return the first valid selector
      const result = idSelector || unitToReplaceIdSelector || unitIdSelector;
      return result;
    } catch (error) {
      console.warn("Could not get unit ID selector from form context:", error);
      return null;
    }
  }, [getFieldValue]);

  // Always call useRecoilValue with either the real selector or a dummy atom
  const recoilValue = useRecoilValue(unitIdSelector || dummyAtom);

  // Get the actual unit ID value (only use it if we had a real selector)
  const actualUnitId = unitIdSelector ? recoilValue : null;

  // Also get the unitToReplaceId value for replacement scenarios
  const unitToReplaceIdSelector = getFieldValue("__unitToReplaceId");
  const unitToReplaceId = useRecoilValue(unitToReplaceIdSelector || dummyAtom);

  // Validate UUID (skip querying with dummy ids like "dummyId_...")
  const validUnitId = React.useMemo(() => {
    // Check if we have a real unitToReplaceId when actualUnitId is dummy
    const unitToReplaceIdValue = unitToReplaceIdSelector ? unitToReplaceId : null;

    // If actualUnitId is a dummy ID and we have a unitToReplaceId, use that instead
    let idToValidate = actualUnitId;
    if (
      typeof actualUnitId === "string" &&
      actualUnitId.startsWith("dummyId_") &&
      typeof unitToReplaceIdValue === "string" &&
      unitToReplaceIdValue.trim() !== ""
    ) {
      idToValidate = unitToReplaceIdValue;
    }

    if (typeof idToValidate !== "string") return undefined;
    // More flexible UUID regex that accepts Microsoft GUID format as well
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const isValid = uuidRegex.test(idToValidate);

    return isValid ? idToValidate : undefined;
  }, [actualUnitId, unitToReplaceIdSelector, unitToReplaceId]);

  const isCreateMode = !validUnitId;

  // Fetch matrix parameters from GraphQL only with a valid UUID
  const {
    data: matrixData,
    loading: matrixLoading,
    error: matrixError,
  } = useGetUnitMatrixParametersQuery({
    variables: { id: validUnitId ?? "00000000-0000-0000-0000-000000000000" },
    skip: !validUnitId,
    errorPolicy: "all",
  });

  // Number of Zw impedance columns (inferred from data or default 1)
  const [zwColumnCount, setZwColumnCount] = React.useState(1);

  // Track if user has made any edits (must be declared before matrixParameters useMemo)
  const hasUserEditedRef = React.useRef(false);

  // Track the current unit ID to detect changes
  const currentUnitIdRef = React.useRef(actualUnitId);

  // Parse the matrix parameters from the JSON string - simplified approach
  const matrixParameters = React.useMemo((): MatrixParametersData | null => {
    // Priority: GraphQL data first (most authoritative), then state
    if (matrixData?.unit?.matrixParameters) {
      const graphqlData = matrixData.unit.matrixParameters;
      const flatRows = flattenRows(graphqlData.taMin, graphqlData.taMax, graphqlData.taRange ?? []);
      // Infer Zw column count from data — only update when user hasn't edited yet
      const maxCols = flatRows.reduce((max, row) => Math.max(max, (row.effectiveImpedances ?? []).length), 1);
      if (!hasUserEditedRef.current) {
        setZwColumnCount(maxCols);
      }
      // Use zwValues from the backend directly; fall back to legacy extraction for old data
      const zwValues: (string | number)[] =
        graphqlData.zwValues && graphqlData.zwValues.length > 0
          ? (graphqlData.zwValues as (string | number)[])
          : extractZwValuesFromLegacyData(flatRows);
      return { taRange: flatRows.length > 0 ? flatRows : [createEmptyTimePowerParameters(maxCols)], zwValues };
    }

    // Fallback to state if no GraphQL data
    if (state && typeof state === "string" && state.trim() !== "") {
      try {
        const parsed = JSON.parse(state) as MatrixParametersData;
        const flatRows = flattenRows(parsed.taMin, parsed.taMax, parsed.taRange ?? []);
        const maxCols = flatRows.reduce((max, row) => Math.max(max, (row.effectiveImpedances ?? []).length), 1);
        if (!hasUserEditedRef.current) {
          setZwColumnCount(maxCols);
        }
        const zwValues = parsed.zwValues ?? extractZwValuesFromLegacyData(flatRows);
        return { taRange: flatRows.length > 0 ? flatRows : [createEmptyTimePowerParameters(maxCols)], zwValues };
      } catch (error) {}
    }

    // Otherwise return null to use default values in UI without persisting yet
    return null;
  }, [matrixData, state]);

  // State for tracking trailing characters (decimal points and zeros) for each input
  const [trailingChars, setTrailingChars] = React.useState<Record<string, string>>({});

  const {
    validateRow,
    hasFieldError,
    getFieldErrorText,
    hasRowError,
    getRowErrorText,
    validateAllRows,
    validateTAOrdering,
    getZwErrors,
  } = useMatrixValidation();

  const { validateAndParseInput, decimalSeparator } = useDecimalInputValidation("de-DE");

  // Initialize form state with parsed data or default values (kept for editing state)
  const [formData, setFormData] = React.useState<MatrixParametersData>(() => {
    return matrixParameters || { taRange: [createEmptyTimePowerParameters(zwColumnCount)] };
  });

  // Auto-computed read-only rows derived from the flat taRange editing rows.
  const { computedMin, computedMax } = React.useMemo(
    () => computeMinMax(formData.taRange ?? [], zwColumnCount),
    [formData.taRange, zwColumnCount]
  );

  // Update form data when unit changes or when we get new data
  React.useEffect(() => {
    const isUnitChange = currentUnitIdRef.current !== actualUnitId;

    if (isUnitChange) {
      // Reset everything when unit changes
      hasUserEditedRef.current = false;
      currentUnitIdRef.current = actualUnitId;

      if (matrixParameters) {
        // New unit with data - load and immediately save to form field
        setFormData(matrixParameters);
        // Save loaded data to form field for edit mode
        const cleanedData = cleanMatrixParametersForSave(matrixParameters);
        const jsonString = JSON.stringify(cleanedData);
        setstate(jsonString);
      } else {
        // New unit without data, use defaults
        setFormData({ taRange: [createEmptyTimePowerParameters(zwColumnCount)] });
      }
    } else if (matrixParameters && !hasUserEditedRef.current) {
      // Same unit, got new data, and user hasn't edited - update formData
      setFormData(matrixParameters);
      // Also save to form field in case this is edit mode data loading
      const cleanedData = cleanMatrixParametersForSave(matrixParameters);
      const jsonString = JSON.stringify(cleanedData);
      setstate(jsonString);
    }
  }, [actualUnitId, matrixParameters, setstate, zwColumnCount]);

  // Derived data to render: inject computed min/max into the table shape
  const displayData: MatrixParametersData | null = React.useMemo(() => {
    return {
      taMin: computedMin,
      taMax: computedMax,
      taRange: formData.taRange,
      zwValues: formData.zwValues,
    };
  }, [computedMin, computedMax, formData.taRange, formData.zwValues]);

  // Validate all rows when formData changes (including initial load)
  React.useEffect(() => {
    validateAllRows(formData);
    if (displayData) validateTAOrdering(displayData);
  }, [formData, displayData, validateAllRows, validateTAOrdering]);

  // Save form data to state when user makes changes
  React.useEffect(() => {
    // Only save if user has actually made edits
    if (hasUserEditedRef.current) {
      const cleanedData = cleanMatrixParametersForSave(formData);
      const jsonString = JSON.stringify(cleanedData);
      setstate(jsonString);
    }
  }, [formData, setstate]);

  // Handle input changes for taRange array with enhanced validation
  const handleRangeParameterChange = useCallback(
    (params: { index: number; field: keyof TimePowerParameters; value: string; zwIndex?: number }) => {
      const { index, field, value, zwIndex } = params;

      // Zw impedance columns use boolean checkboxes — bypass decimal validation
      if (field === "effectiveImpedances" && zwIndex !== undefined) {
        hasUserEditedRef.current = true;
        const boolValue = value === "true";
        setFormData((prev) => {
          const newRange =
            prev.taRange?.map((item, i) => {
              if (i === index) {
                const impedances = [...(item.effectiveImpedances ?? [])];
                impedances[zwIndex] = boolValue;
                const newItem = { ...item, effectiveImpedances: impedances };
                validateRow("taRange", newItem, index);
                return newItem;
              }
              return item;
            }) || [];
          return { ...prev, taRange: newRange };
        });
        return;
      }

      const fieldKey = `taRange-${index}-${String(field)}`;
      const currentValue = formData.taRange?.[index]?.[field];
      const currentNumericValue = typeof currentValue === "number" ? currentValue : undefined;
      const validation = validateAndParseInput(value, currentNumericValue, P_FIELDS.has(field));

      if (validation.isValid) {
        hasUserEditedRef.current = true;

        // Update trailing characters for display
        setTrailingChars((prev) => ({
          ...prev,
          [fieldKey]: validation.trailingChars,
        }));

        const newValue = value === "" ? "" : validation.parsedValue !== undefined ? validation.parsedValue : value;

        setFormData((prev) => {
          const newRange =
            prev.taRange?.map((item, i) => {
              if (i === index) {
                let newItem: TimePowerParameters;
                if (field === "effectiveImpedances" && zwIndex !== undefined) {
                  const impedances = [...(item.effectiveImpedances ?? [])];
                  impedances[zwIndex] = newValue;
                  newItem = { ...item, effectiveImpedances: impedances };
                } else {
                  newItem = { ...item, [field]: newValue };
                }
                // Validate the row after updating
                validateRow("taRange", newItem, index);
                return newItem;
              }
              return item;
            }) || [];

          return {
            ...prev,
            taRange: newRange,
          };
        });
      }
    },
    [validateAndParseInput, formData, validateRow]
  );

  // Helper function to get display value with trailing characters
  const getDisplayValue = useCallback(
    (section: "taMin" | "taMax" | "taRange", field: keyof TimePowerParameters, indices?: FieldIndices): string => {
      const { index, zwIndex } = indices ?? {};
      const fieldKey =
        field === "effectiveImpedances" && zwIndex !== undefined
          ? index !== undefined
            ? `${section}-${index}-effectiveImpedances-${zwIndex}`
            : `${section}-effectiveImpedances-${zwIndex}`
          : index !== undefined
          ? `${section}-${index}-${String(field)}`
          : `${section}-${String(field)}`;
      const trailingCharsForField = trailingChars[fieldKey] || "";

      let value: number | string | boolean = "";
      if (field === "effectiveImpedances" && zwIndex !== undefined) {
        // Impedance array element
        let row: TimePowerParameters | undefined;
        if (section === "taRange" && index !== undefined) {
          row = formData.taRange?.[index];
        } else if (section === "taMin") {
          row = computedMin;
        } else if (section === "taMax") {
          row = computedMax;
        }
        const impedanceVal = row?.effectiveImpedances?.[zwIndex];
        if (impedanceVal != null && impedanceVal !== undefined) {
          value = impedanceVal;
        }
      } else if (section === "taRange" && index !== undefined) {
        const rangeItem = formData.taRange?.[index];
        if (rangeItem && rangeItem[field] != null) {
          const fieldValue = rangeItem[field];
          if (fieldValue !== undefined && !Array.isArray(fieldValue)) {
            value = fieldValue;
          }
        }
      } else if (section === "taMin") {
        // Read-only computed row
        const fieldValue = computedMin[field];
        if (fieldValue != null && fieldValue !== undefined && !Array.isArray(fieldValue)) {
          value = fieldValue;
        }
      } else if (section === "taMax") {
        // Read-only computed row
        const fieldValue = computedMax[field];
        if (fieldValue != null && fieldValue !== undefined && !Array.isArray(fieldValue)) {
          value = fieldValue;
        }
      }

      if (value === "" || value == null) {
        return "";
      }

      // No trailing chars for read-only computed rows
      const trailing = section === "taRange" ? trailingCharsForField : "";
      const displayValue = `${value.toString()}${trailing}`;
      return replaceAll(displayValue, ".", decimalSeparator);
    },
    [formData, computedMin, computedMax, trailingChars, decimalSeparator]
  );

  // Callback functions for the DecimalInput component
  const handleInputChange = useCallback(
    (params: {
      section: "taMin" | "taMax" | "taRange";
      field: keyof TimePowerParameters;
      value: string;
      index?: number;
      zwIndex?: number;
    }) => {
      const { section, field, value, index, zwIndex } = params;
      if (section === "taRange" && index !== undefined) {
        handleRangeParameterChange({ index, field, value, zwIndex });
      }
      // taMin and taMax are read-only computed rows — no input handling needed
    },
    [handleRangeParameterChange]
  );

  const handleInputBlur = useCallback(
    (params: {
      section: "taMin" | "taMax" | "taRange";
      field: keyof TimePowerParameters;
      index?: number;
      zwIndex?: number;
    }) => {
      const { section, field, index, zwIndex } = params;
      // Clear trailing characters on blur to clean up display
      const fieldKey =
        field === "effectiveImpedances" && zwIndex !== undefined
          ? index !== undefined
            ? `taRange-${index}-effectiveImpedances-${zwIndex}`
            : `${section}-effectiveImpedances-${zwIndex}`
          : index !== undefined
          ? `taRange-${index}-${String(field)}`
          : `${section}-${String(field)}`;
      setTrailingChars((prev) => ({
        ...prev,
        [fieldKey]: "",
      }));
    },
    []
  );

  // Add new taRange item
  const addRangeItem = () => {
    hasUserEditedRef.current = true;
    setFormData((prev) => ({
      ...prev,
      taRange: [...(prev.taRange || []), createEmptyTimePowerParameters(zwColumnCount)],
    }));
  };

  // Remove taRange item
  const removeRangeItem = (index: number) => {
    hasUserEditedRef.current = true;
    setFormData((prev) => ({
      ...prev,
      taRange: prev.taRange?.filter((_, i) => i !== index) || [],
    }));
  };

  // Add a new Zw impedance column to all rows
  const addZwColumn = () => {
    hasUserEditedRef.current = true;
    setZwColumnCount((prev) => prev + 1);
    setFormData((prev) => ({
      ...prev,
      zwValues: [...(prev.zwValues ?? []), ""],
      taRange: prev.taRange?.map((row) => ({
        ...row,
        effectiveImpedances: [...(row.effectiveImpedances ?? []), false],
      })),
    }));
  };

  // Remove a Zw impedance column from all rows (minimum 1 column)
  const removeZwColumn = (zwIdx: number) => {
    if (zwColumnCount <= 1) return;
    hasUserEditedRef.current = true;
    setZwColumnCount((prev) => prev - 1);
    setFormData((prev) => ({
      ...prev,
      zwValues: (prev.zwValues ?? []).filter((_, i) => i !== zwIdx),
      taRange: prev.taRange?.map((row) => ({
        ...row,
        effectiveImpedances: (row.effectiveImpedances ?? []).filter((_, i) => i !== zwIdx),
      })),
    }));
  };

  // Handle change to the Zw value in a column header
  const handleZwValueChange = useCallback(
    (zwIdx: number, rawValue: string) => {
      const allowedChars = /^[0-9.,]*$/;
      if (!allowedChars.test(rawValue) && rawValue !== "") return;
      hasUserEditedRef.current = true;
      const parsed = validateAndParseInput(rawValue, undefined);
      const fieldKey = `zwValue-${zwIdx}`;
      setTrailingChars((prev) => ({
        ...prev,
        [fieldKey]: parsed.trailingChars,
      }));
      const newValue = rawValue === "" ? "" : parsed.parsedValue !== undefined ? parsed.parsedValue : rawValue;
      setFormData((prev) => {
        const zwValues = [...(prev.zwValues ?? [])];
        zwValues[zwIdx] = newValue;
        return { ...prev, zwValues };
      });
    },
    [validateAndParseInput]
  );

  return (
    <Box className={className} style={{ opacity: disabled ? 0.6 : 1 }} sx={{ backgroundColor: "white", p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>

      {matrixLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Loading matrix parameters...
          </Typography>
        </Box>
      )}

      {matrixError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not load matrix parameters: {matrixError?.message || String(matrixError)}
        </Alert>
      )}

      {/* Visibility control as a form row - only show during upload, not edit */}
      {showVisibilityControls && !isEdit && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, pl: "16px", pt: 0.5 }}>
          <Typography sx={{ minWidth: "200px", fontSize: "18px", fontWeight: 400, color: "text.secondary" }}>
            {translations.pages.certificateUpload.matrixVisibility.configureAccess}:
          </Typography>
          <Tooltip title={getVisibilityTooltip()}>
            <IconButton disableRipple onClick={() => setOpenVisibilityModal(true)} size="small">
              {hasVisibilityGrants() ? <VisibilityIcon sx={{ color: "primary.main" }} /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Only show table when we are in create mode or we have data */}
      {displayData ? (
        <>
          <StyledTableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledColumnHeaderCell></StyledColumnHeaderCell>
                  <StyledColumnHeaderCell align="center"></StyledColumnHeaderCell>
                  <StyledColumnHeaderCell
                    align="center"
                    dangerouslySetInnerHTML={{ __html: `T${subscript("A")} [s]` }}
                  ></StyledColumnHeaderCell>
                  <StyledColumnHeaderCell
                    align="center"
                    dangerouslySetInnerHTML={{ __html: `P${subscript("min,dyn")} [kW]` }}
                  ></StyledColumnHeaderCell>
                  <StyledColumnHeaderCell
                    align="center"
                    dangerouslySetInnerHTML={{ __html: `P${subscript("max,dyn")} [kW]` }}
                  ></StyledColumnHeaderCell>
                  <StyledColumnHeaderCell
                    align="center"
                    dangerouslySetInnerHTML={{ __html: `P${subscript("grenzneg,min")} [kW]` }}
                  ></StyledColumnHeaderCell>
                  <StyledColumnHeaderCell
                    align="center"
                    dangerouslySetInnerHTML={{ __html: `P${subscript("grenzneg,max")} [kW]` }}
                  ></StyledColumnHeaderCell>
                  <StyledColumnHeaderCell
                    align="center"
                    dangerouslySetInnerHTML={{ __html: `P${subscript("grenzpos,min")} [kW]` }}
                  ></StyledColumnHeaderCell>
                  <StyledColumnHeaderCell
                    align="center"
                    dangerouslySetInnerHTML={{ __html: `P${subscript("grenzpos,max")} [kW]` }}
                  ></StyledColumnHeaderCell>
                  {/* Dynamic Zw impedance column headers with value input */}
                  {Array.from({ length: zwColumnCount }, (_, zwIdx) => (
                    <StyledColumnHeaderCell key={`zw-header-${zwIdx}`} align="center">
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {zwIdx === 0 ? (
                            <>
                              Z<sub>w,nom</sub> [p.u.]
                            </>
                          ) : (
                            <>
                              Z<sub>w,{zwIdx + 1}</sub> [p.u.]
                            </>
                          )}
                          {!disabled && zwColumnCount > 1 && zwIdx > 0 && (
                            <Tooltip placement="top" title={translations.globals.button.remove}>
                              <IconButton size="small" onClick={() => removeZwColumn(zwIdx)} sx={{ p: 0, ml: 0.5 }}>
                                <RemoveCircleOutline sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <StyledTextField
                          type="text"
                          size="small"
                          value={
                            formData.zwValues?.[zwIdx] != null && formData.zwValues[zwIdx] !== ""
                              ? replaceAll(
                                  `${String(formData.zwValues[zwIdx])}${trailingChars[`zwValue-${zwIdx}`] || ""}`,
                                  ".",
                                  decimalSeparator
                                )
                              : ""
                          }
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleZwValueChange(zwIdx, e.target.value)
                          }
                          disabled={disabled}
                          error={!disabled && (formData.zwValues?.[zwIdx] == null || formData.zwValues[zwIdx] === "")}
                          sx={{ width: 80 }}
                        />
                      </Box>
                    </StyledColumnHeaderCell>
                  ))}
                  {/* Add Zw column button in header */}
                  {!disabled && (
                    <StyledColumnHeaderCell align="center" sx={{ width: 40, p: 0 }}>
                      <>
                        <Tooltip placement="top" title={translations.globals.button.add}>
                          <IconButton size="small" onClick={addZwColumn}>
                            <Add sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>{" "}
                        {translations.pages.certificateUpload.addEffectiveImpedance}
                      </>
                    </StyledColumnHeaderCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* taMin row (always present, read-only - computed from T_A rows) */}
                {displayData.taMin && (
                  <MatrixParameterRow
                    rowLabel={`T${subscript("A,min")}`}
                    section="taMin"
                    data={displayData.taMin}
                    zwColumnCount={zwColumnCount}
                    showAddZwColumn={!disabled}
                    hasRowError={hasRowError}
                    getRowErrorText={getRowErrorText}
                    getDisplayValue={getDisplayValue}
                    handleInputChange={handleInputChange}
                    handleInputBlur={handleInputBlur}
                    hasFieldError={hasFieldError}
                    getFieldErrorText={getFieldErrorText}
                    disabled
                  />
                )}

                {/* taRange rows (dynamic) - T_A,1 (index 0) is mandatory and non-deletable */}
                {displayData.taRange?.map((rangeItem, index) => (
                  <MatrixParameterRow
                    key={`range-${index}`}
                    rowLabel={`T${subscript(`A,${index + 1}`)}`}
                    section="taRange"
                    index={index}
                    data={rangeItem}
                    zwColumnCount={zwColumnCount}
                    showAddZwColumn={!disabled}
                    hasRowError={hasRowError}
                    getRowErrorText={getRowErrorText}
                    getDisplayValue={getDisplayValue}
                    handleInputChange={handleInputChange}
                    handleInputBlur={handleInputBlur}
                    hasFieldError={hasFieldError}
                    getFieldErrorText={getFieldErrorText}
                    disabled={disabled}
                    onDelete={index === 0 ? undefined : () => removeRangeItem(index)}
                  />
                ))}

                {/* taMax row (always present, read-only - computed from T_A rows) */}
                {displayData.taMax && (
                  <MatrixParameterRow
                    rowLabel={`T${subscript("A,max")}`}
                    section="taMax"
                    data={displayData.taMax}
                    zwColumnCount={zwColumnCount}
                    showAddZwColumn={!disabled}
                    hasRowError={hasRowError}
                    getRowErrorText={getRowErrorText}
                    getDisplayValue={getDisplayValue}
                    handleInputChange={handleInputChange}
                    handleInputBlur={handleInputBlur}
                    hasFieldError={hasFieldError}
                    getFieldErrorText={getFieldErrorText}
                    disabled
                  />
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </>
      ) : (
        !isCreateMode &&
        !matrixLoading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Keine Matrix-Parameter vorhanden.
          </Alert>
        )
      )}

      {/* Add Range Button below table */}
      {displayData && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <StyledAddRangeButton
            size="small"
            startIcon={<Add />}
            onClick={addRangeItem}
            disabled={disabled}
            variant="outlined"
          >
            {translations.pages.certificateUpload.addStartUpTimeConstant}
          </StyledAddRangeButton>
        </Box>
      )}

      {(errorManagerRes.error || getZwErrors(formData).length > 0) && (
        <>
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 1 }}
            dangerouslySetInnerHTML={{ __html: `${errorManagerRes.helperText}` }}
          />
          {getZwErrors(formData).length > 0 && (
            <>
              {getZwErrors(formData).map((error, index) => (
                <>
                  <span
                    key={index}
                    style={{ color: "#d32f2f", fontSize: "1rem" }}
                    dangerouslySetInnerHTML={{ __html: error }}
                  />
                  <br />
                </>
              ))}
            </>
          )}
        </>
      )}

      {/* Matrix Parameter Visibility Modal */}
      <MatrixParameterVisibilityModal
        open={openVisibilityModal}
        currentRoles={currentRoles}
        currentTenantIds={currentTenantIds}
        onClose={() => setOpenVisibilityModal(false)}
        onSave={handleVisibilitySave}
      />
    </Box>
  );
};

export default MatrixParameters;
